import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { graphqlClient } from '../../services/graphqlClient';
import type { Document, File as FileType } from '../../graphql/types';
import { sortReviewDocuments, toReviewDocument } from './reviewModel';
import type { ReviewDocument } from './reviewModel';

/**
 * Loads the recorded documents of a Title Search for Review mode, the way
 * `RecordedDocumentReviewStep` does: loans, then files, then documents, kept
 * fresh by the `onDocumentStatusChanged` subscription with reconnect and
 * re-fetch on error (Requirements 10.2, 10.4, 10.5 of the title-search spec).
 *
 * Reviewer actions are not held here, so a pushed update refreshes extracted
 * values without losing the reviewer's work.
 */

const isPreviewMode =
  import.meta.env.VITE_PREVIEW === 'true' || !import.meta.env.VITE_COGNITO_USER_POOL_ID;

const MAX_RECONNECT_ATTEMPTS = 3;
const FILE_REFRESH_DELAY_MS = 1500;

export interface UseReviewDocumentsResult {
  documents: ReviewDocument[];
  /** Source files by id, for loading the PDF of a document. */
  files: Record<string, FileType>;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

async function currentTenantId(): Promise<string> {
  const session = await fetchAuthSession();
  return (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
}

export function useReviewDocuments(jobId: string): UseReviewDocumentsResult {
  const [documents, setDocuments] = useState<ReviewDocument[]>([]);
  const [files, setFiles] = useState<Record<string, FileType>>({});
  const [loading, setLoading] = useState(!isPreviewMode);
  const [error, setError] = useState<string | null>(null);

  const jobIdRef = useRef(jobId);
  jobIdRef.current = jobId;

  const load = useCallback(async () => {
    if (isPreviewMode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const tenantId = await currentTenantId();
      const loans = await graphqlClient.queries.listLoansByJob(jobIdRef.current, tenantId);
      const collectedFiles: Record<string, FileType> = {};
      const collectedDocs: ReviewDocument[] = [];
      for (const loan of loans.items) {
        const filesData = await graphqlClient.queries.listFilesByLoan(
          loan.loanId,
          jobIdRef.current,
          tenantId
        );
        for (const file of filesData.items) {
          collectedFiles[file.fileId] = file;
          const docsData = await graphqlClient.queries.listDocumentsByFile(
            file.fileId,
            loan.loanId,
            jobIdRef.current,
            tenantId
          );
          for (const doc of docsData.items) collectedDocs.push(toReviewDocument(doc));
        }
      }
      setFiles(collectedFiles);
      setDocuments(sortReviewDocuments(collectedDocs));
    } catch (err) {
      console.error('[ReviewMode] Failed to load documents:', err);
      setError('The recorded documents could not be loaded. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, jobId]);

  // Subscribe per loan; on error reconnect and re-fetch to reconcile.
  useEffect(() => {
    if (isPreviewMode) return;
    let cancelled = false;
    let handles: Array<{ unsubscribe: () => void }> = [];
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    // A pushed document does not carry its file's status, so re-read the files
    // shortly after the last update. Review mode uses the file statuses to hold
    // Finalize back while a file is still processing.
    const scheduleRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => void load(), FILE_REFRESH_DELAY_MS);
    };

    const upsert = (doc: Document) => {
      const next = toReviewDocument(doc);
      setDocuments((prev) => {
        const index = prev.findIndex((d) => d.documentId === next.documentId);
        if (index < 0) return sortReviewDocuments([...prev, next]);
        const copy = [...prev];
        copy[index] = next;
        return sortReviewDocuments(copy);
      });
    };

    const connect = async () => {
      try {
        const tenantId = await currentTenantId();
        const loans = await graphqlClient.queries.listLoansByJob(jobIdRef.current, tenantId);
        if (cancelled) return;
        handles.forEach((h) => h.unsubscribe());
        handles = [];
        for (const loan of loans.items) {
          const sub = graphqlClient.subscriptions.onDocumentStatusChanged(
            loan.loanId,
            jobIdRef.current,
            tenantId
          );
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const handle = (sub as any).subscribe({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            next: ({ data }: any) => {
              const doc = data?.onDocumentStatusChanged as Document | undefined;
              if (doc && doc.jobId === jobIdRef.current) {
                attempts = 0;
                upsert(doc);
                scheduleRefresh();
              }
            },
            error: (err: unknown) => {
              console.error('[ReviewMode] Document subscription error:', err);
              attempts += 1;
              if (!cancelled && attempts <= MAX_RECONNECT_ATTEMPTS) {
                timer = setTimeout(() => {
                  void load();
                  void connect();
                }, 1000 * attempts);
              }
            },
          });
          handles.push(handle);
        }
      } catch (err) {
        console.error('[ReviewMode] Failed to set up document subscriptions:', err);
      }
    };

    void connect();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (refreshTimer) clearTimeout(refreshTimer);
      handles.forEach((h) => h.unsubscribe());
      handles = [];
    };
  }, [load, jobId]);

  return { documents, files, loading, error, reload: load };
}
